/**
 * Corpus Reader (Task 189) — the public read-only "Biblioteca" view.
 *
 * Self-contained alongside the chat (App renders this when the route is /corpus).
 * Extends the existing walnut/gold arrow brand: quiet, study-dense, reusing the
 * Wordmark glyph, the answer-markdown renderer for cleaned section text, and the
 * design tokens. Per Diego's decision (2026-06-06) it shows EVERY cleaned chapter
 * with no RAG-state distinction — the goal is reading the curated corpus locally.
 *
 * Routes (via useCorpusRoute):
 *   /corpus                 → library index (hero + book/chapter nav)
 *   /corpus/:book/:chapter  → chapter reader (compact nav + reading pane, #anchor)
 */
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { BowSigil } from "../components/Wordmark";
import { Topbar } from "../components/Topbar";
import { CorpusLibrary } from "./CorpusLibrary";
import { FlechasPane } from "./FlechasPane";
import { FlechasShelf } from "./FlechasShelf";
import { compareFlechaPresentation } from "./flechas";
import { CorpusMarkdown } from "./CorpusMarkdown";
import { useReducedMotion } from "../hooks/use-reduced-motion";
import type { Language } from "../strings";
import type { Strings } from "../strings/types";
import { BookmarkFlight, type Flight } from "./BookmarkFlight";
import type { CorpusIndexBook, CorpusIndexShort, CorpusManifest } from "./corpus-types";
import type { ReadingBookmark } from "./reading-record";
import { ReadabilityControls } from "./ReadabilityControls";
import { SelectionBookmarkMenu } from "./SelectionBookmarkMenu";
import { useCorpusChapter, useCorpusManifest, useLatestBook } from "./use-corpus-data";
import { useReaderPrefs } from "./use-reader-prefs";
import { PopularQuestions } from "../components/PopularQuestions";
import { GlossaryCard } from "./GlossaryCard";
import { usePrismData } from "./use-prism-data";
import { useReadingRecord, type SaveBookmarkInput } from "./use-reading-record";
import { useTermGlossary } from "./use-term-glossary";
import { useTextSelection, type SelectionGeometry } from "./use-text-selection";
import {
  connectionsPath,
  corpusPath,
  corpusShortPath,
  softNavigate,
  type CorpusRoute,
} from "./use-corpus-route";

export interface CorpusReaderProps {
  route: CorpusRoute;
  navigate: (to: string) => void;
  language: Language;
  onLanguage: (lang: Language) => void;
  strings: Strings;
  /** Task 191: "ask about this" — seed a fresh chat composer from a section. */
  onAsk: (prompt: string) => void;
  /** Task 224: open the shared About dialog (unified topbar). */
  onAbout: () => void;
}

/** The reader's actual scroll container (`.app--corpus` is `overflow-y:auto`;
 *  the chat shell pins the window, so `window.scrollY` is always 0 here).
 *  Walks up from the pane so the coupling is structural, not class-name-based. */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement ?? null; n !== null; n = n.parentElement) {
    const overflowY = window.getComputedStyle(n).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return n;
  }
  return null;
}

/** A chapter's display heading: "{name} · {label}" when the chapter carries a
 *  display label (e.g. a patriarch name for t12patriarcas), else "{name} {chapter}". */
function chapterHeading(name: string, chapter: number, label?: string | null): string {
  return label ? `${name} · ${label}` : `${name} ${chapter}`;
}

/** Human-friendly label for a chunk's lead heading (strips the `[..]` bracket). */
function sectionLabel(headings: string[]): string | null {
  const h = headings[0];
  if (!h) return null;
  const m = /^\[([^\]]+)\]\s*(.*)$/.exec(h);
  if (m) {
    const after = (m[2] ?? "").trim();
    if (after) return after;
    const inner = (m[1] ?? "").trim();
    return inner ? inner.charAt(0) + inner.slice(1).toLowerCase() : null;
  }
  return h.trim() || null;
}

/** Shared book → chapter-chips navigation, used big on the library index and
 * compact in the chapter view. */
function BookNav(props: {
  books: CorpusIndexBook[];
  activeBook: string | null;
  activeChapter: number | null;
  compact: boolean;
  strings: Strings;
  onChapter: (book: string, chapter: number) => void;
  /** Task 224: the saved reading point — its chapter chip gets a gold dot. */
  bookmark?: ReadingBookmark | null;
}): JSX.Element {
  const { books, activeBook, activeChapter, compact, onChapter, strings, bookmark } = props;
  return (
    <nav className={`creader-nav ${compact ? "creader-nav--compact" : ""}`} aria-label={strings.corpusBookListLabel}>
      {books.map((book, bookIndex) => {
        const isActiveBook = book.slug === activeBook;
        return (
          <section
            key={book.slug}
            className={`creader-nav__book ${isActiveBook ? "is-active" : ""}`}
            // Task 208: stagger the library plaques' entrance (CSS reads --reveal-i).
            style={compact ? undefined : { ["--reveal-i" as string]: String(bookIndex) }}
          >
            <h3 className="creader-nav__bookname">
              {!compact ? <BowSigil className="creader-nav__booksigil" /> : null}
              <span className="creader-nav__booktitle">{book.name}</span>
              {!compact ? (
                <span className="creader-nav__count">{strings.corpusChapterCount(book.chapters.length)}</span>
              ) : null}
            </h3>
            <div className="creader-nav__chips">
              {book.chapters.map((ch) => {
                const active = isActiveBook && ch.chapter === activeChapter;
                const marked = bookmark != null && bookmark.book === book.slug && bookmark.chapter === ch.chapter;
                return (
                  <button
                    key={ch.chapter}
                    type="button"
                    className={`creader-chip ${ch.label ? "creader-chip--named" : ""} ${active ? "is-active" : ""}${marked ? " has-mark" : ""}`}
                    aria-current={active ? "page" : undefined}
                    onClick={() => onChapter(book.slug, ch.chapter)}
                    title={marked ? strings.corpusBookmarkPinLabel : chapterHeading(book.name, ch.chapter, ch.label)}
                  >
                    {ch.label ?? ch.chapter}
                    {marked ? <span className="creader-chip__dot" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </nav>
  );
}

/** The reading pane for one chapter. */
function ChapterPane(props: {
  book: string;
  chapter: number;
  anchor: string | null;
  scale: number;
  strings: Strings;
  language: Language;
  onAsk: (prompt: string) => void;
  /** Task 204: persist a reading point from the current text selection. */
  onSaveBookmark: (input: SaveBookmarkInput) => void;
  /** Task 204: the current reading point, to mark its chunk with a pin. */
  activeBookmark: ReadingBookmark | null;
  /** Task 204: re-scroll to the active bookmark when this changes (jump-back). */
  jumpNonce: number;
}): JSX.Element {
  const { book, chapter, anchor, scale, strings, language, onAsk, onSaveBookmark, activeBookmark, jumpNonce } = props;
  const content = useCorpusChapter(book, chapter);
  const paneRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const { selection, dismiss } = useTextSelection(paneRef);
  // The transient flight overlay (one per save). `key` remounts BookmarkFlight.
  const [flight, setFlight] = useState<Flight | null>(null);
  const flightSeq = useRef(0);
  // Toggled true briefly on save so the sr-only live region announces it.
  const [announced, setAnnounced] = useState(false);
  // Task 209: reading-progress hairline. Driven via direct DOM transform (ref),
  // NOT state — a setState per scroll frame would re-render ~25 markdown chunks.
  const progressRef = useRef<HTMLDivElement>(null);
  // Task 222: Hebrew-term hover cards (el Glosario Vivo).
  const glossary = useTermGlossary(paneRef, content.state === "ready");
  // Task 220 POC (the Prism): layer annotations + the active layer filter.
  // `prismSettled` (Task 223): scroll effects wait for it — prism chips shift
  // layout when they arrive, and a scroll performed before that drifts.
  const { index: prism, settled: prismSettled } = usePrismData();
  const [activeLayers, setActiveLayers] = useState<ReadonlySet<string>>(new Set());
  const toggleLayer = (slug: string): void => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Opening a chapter must start at the top of the text. The reader reuses the
  // library's scroll container (.app--corpus), so without this the chapter
  // opened wherever the library wall had been scrolled to. Waits for "ready"
  // because the loading branch renders no pane (paneRef is null on mount).
  // Deep links with an anchor are exempt — the anchor effect below positions
  // the scroll itself.
  useEffect(() => {
    if (anchor !== null || content.state !== "ready") return;
    scrollParentOf(paneRef.current)?.scrollTo({ top: 0 });
  }, [book, chapter, anchor, content.state]);

  // Scroll to + briefly highlight the anchored section once content is ready.
  // DEFER past layout: with ~25 chunks of markdown, scrolling on the same tick the
  // content becomes "ready" runs before the chunks above have their final height,
  // so the target ends up at the wrong offset (it scrolled to where the element
  // momentarily was, then layout pushed it down). Two rAFs let layout settle first.
  // Task 223: prismSettled is a dep — when the prism artifact arrives it grows
  // the header (filter row) and headings (chips), shifting layout; re-running
  // the scroll on settle lands on the FINAL geometry.
  useEffect(() => {
    if (content.state !== "ready" || !anchor) return;
    let raf2 = 0;
    let flash = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = paneRef.current?.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(anchor)}"]`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("is-flash");
        flash = window.setTimeout(() => el.classList.remove("is-flash"), 1600);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(flash);
    };
  }, [content.state, anchor, prismSettled]);

  // Jump-back (Task 204): re-scroll to the saved reading point on demand, even
  // when its anchor already equals the URL anchor (so the pill always works).
  // jumpNonce starts at 0 and only the bumps (1+) should scroll; the handled-ref
  // lets the effect also depend on content.state (cross-chapter jump arrives
  // before the chapter is ready) without double-scrolling once it is.
  const handledJumpRef = useRef(0);
  useEffect(() => {
    if (jumpNonce === 0 || jumpNonce === handledJumpRef.current) return;
    // Task 223: wait for BOTH content and the prism artifact — prism chips
    // shift layout when they arrive, so an earlier scroll lands above target.
    if (content.state !== "ready" || !prismSettled) return;
    if (activeBookmark === null || activeBookmark.book !== book || activeBookmark.chapter !== chapter) return;
    handledJumpRef.current = jumpNonce;

    // Flash the bookmarked chunk for orientation regardless of scroll method.
    const target = activeBookmark.anchor;
    const el =
      target !== undefined
        ? paneRef.current?.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(target)}"]`) ?? null
        : null;
    let flash = 0;
    if (el !== null) {
      el.classList.add("is-flash");
      flash = window.setTimeout(() => el.classList.remove("is-flash"), 1600);
    }

    // PRIMARY (Task 223): the EXACT reading position — container scrollTop
    // captured at save time. A chunk is ~20 minutes of teaching; its top can be
    // far above the highlighted text, so the anchor is only the fallback.
    const top = activeBookmark.scrollY;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (top !== undefined) {
          const sc = scrollParentOf(paneRef.current);
          if (sc !== null) sc.scrollTo({ top, behavior: "smooth" });
          else window.scrollTo({ top, behavior: "smooth" });
        } else if (el !== null) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(flash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpNonce, content.state, prismSettled]);

  // Task 209: track scroll through the chapter on the reader's scroll container
  // (.app--corpus, not the window) and advance the gold hairline. rAF-coalesced;
  // passive listener; writes only a transform so nothing reflows.
  useEffect(() => {
    if (content.state !== "ready") return;
    const sc = scrollParentOf(paneRef.current);
    if (sc === null) return;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const bar = progressRef.current;
      if (bar === null) return;
      const max = sc.scrollHeight - sc.clientHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, sc.scrollTop / max)) : 0;
      bar.style.transform = `scaleX(${p})`;
    };
    const onScroll = (): void => {
      if (raf === 0) raf = requestAnimationFrame(update);
    };
    update();
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf !== 0) cancelAnimationFrame(raf);
      sc.removeEventListener("scroll", onScroll);
    };
  }, [content.state]);

  if (content.state === "loading") return <p className="creader-note">{strings.corpusLoading}</p>;
  if (content.state === "empty") return <p className="creader-note">{strings.corpusEmptyChapter}</p>;
  if (content.state === "error") return <p className="creader-note creader-note--err">{strings.corpusError}</p>;

  const data = content.data;
  // `sel` is the geometry snapshot the menu rendered with — NOT the live state,
  // which a touch tap may already have collapsed to null by the time this runs.
  const handleSave = (sel: SelectionGeometry): void => {
    onSaveBookmark({
      book,
      chapter,
      anchor: sel.anchor,
      snippet: sel.snippet,
      // The reader scrolls .app--corpus, not the window — window.scrollY is 0 here.
      scrollY: Math.round(scrollParentOf(paneRef.current)?.scrollTop ?? window.scrollY),
    });
    // Launch the ember from just above the selection's center to its end top-right.
    flightSeq.current += 1;
    setFlight({
      key: flightSeq.current,
      from: { x: sel.bounds.left + sel.bounds.width / 2, y: sel.bounds.top - 14 },
      to: sel.endTopRight,
    });
    dismiss();
    setAnnounced(true);
    window.setTimeout(() => setAnnounced(false), 1200);
  };

  const isBookmarked = (chunkAnchor: string): boolean =>
    activeBookmark !== null &&
    activeBookmark.book === book &&
    activeBookmark.chapter === chapter &&
    activeBookmark.anchor === chunkAnchor;

  return (
    <div className="creader-pane" ref={paneRef} style={{ ["--reader-scale" as string]: String(scale) }}>
      {/* Task 209: fixed reading-progress hairline (transform-driven via ref). */}
      <div className="creader-progress" aria-hidden="true">
        <div ref={progressRef} className="creader-progress__bar" />
      </div>
      <header className="creader-pane__head" data-chapter={data.chapter}>
        <span className="creader-pane__kicker">{strings.corpusChapterLabel}</span>
        <h1 className="creader-pane__title">
          {chapterHeading(data.name, data.chapter, data.label)}
        </h1>
        {/* Task 209: the same loosed-arrow motif as the library hero, left-drawn. */}
        <div className="creader-pane__arrow" aria-hidden="true">
          <span className="creader-hero__arrowline" />
          <span className="creader-hero__arrowhead" />
        </div>
        {/* Task 215: the reader shows every cleaned chapter, but the chat only
            cites promoted ones (rag/collections.json ready_chapters) — set the
            expectation up front so "Pregunta sobre esto" can't feel broken. */}
        {!data.status.promoted ? (
          <p className="creader-pane__chatnote">
            <BowSigil className="creader-pane__chatnote-sigil" />
            {strings.corpusNotIndexedNotice}
          </p>
        ) : null}
        {/* Task 220 POC: the Prism filter — shown only when this chapter has
            human-reviewed layer annotations. Filtering DIMS (never hides) so
            context stays readable. */}
        {/* Task 224: this chapter's place in the connections graph. */}
        <a
          className="creader-pane__connections"
          href={connectionsPath(`c:${book}-${chapter}`)}
          onClick={(e) => {
            e.preventDefault();
            softNavigate(connectionsPath(`c:${book}-${chapter}`));
          }}
        >
          {strings.citationViewConnections} ↗
        </a>
        {prism !== null && data.chunks.some((c) => prism.anchors.has(c.anchor)) ? (
          <div className="prism-filter" role="group" aria-label={strings.corpusPrismFilterLabel}>
            <span className="prism-filter__label">{strings.corpusPrismFilterLabel}</span>
            {prism.defs.map((def) => (
              <button
                key={def.slug}
                type="button"
                className={`prism-chip prism-chip--btn${activeLayers.has(def.slug) ? " is-active" : ""}`}
                style={{ ["--prism-hue" as string]: def.hue }}
                aria-pressed={activeLayers.has(def.slug)}
                title={def.display_es}
                onClick={() => toggleLayer(def.slug)}
              >
                <span className="prism-chip__glyph">{def.glyph}</span>
                {def.hebrew}
              </button>
            ))}
          </div>
        ) : null}
      </header>
      {data.chunks.map((chunk) => (
        <article
          key={chunk.anchor}
          id={chunk.anchor}
          data-anchor={chunk.anchor}
          className={`creader-chunk${isBookmarked(chunk.anchor) ? " has-bookmark" : ""}`}
        >
          {isBookmarked(chunk.anchor) ? (
            <span className="creader-chunk__pin" title={strings.corpusBookmarkPinLabel} aria-hidden="true">
              <BowSigil className="creader-chunk__pin-sigil" />
            </span>
          ) : null}
          <div className="creader-chunk__source">
            <BowSigil className="creader-chunk__sigil" />
            <span className="creader-chunk__src-text">
              {chunk.source.title ?? chunk.slug}
              {chunk.source.timestamp_label ? <span className="creader-chunk__ts"> · {chunk.source.timestamp_label}</span> : null}
            </span>
            {chunk.source.url ? (
              <a className="creader-chunk__yt" href={chunk.source.url} target="_blank" rel="noopener noreferrer">
                {strings.corpusWatchOnYouTube} ↗
              </a>
            ) : null}
          </div>
          <div className="creader-md">
            {/* Task 214: corpus-scoped renderer — bracket headings become on-brand
                section markers; chat's AnswerMarkdown stays untouched.
                Task 220 POC: prism layer chips + filter dimming per section. */}
            <CorpusMarkdown text={chunk.md} anchor={chunk.anchor} prism={prism} activeLayers={activeLayers} />
          </div>
          <div className="creader-chunk__foot">
            <button
              type="button"
              className="creader-chunk__ask"
              onClick={() =>
                onAsk(
                  strings.corpusAskPrompt(
                    data.label ? `${data.name} · ${data.label}` : data.name,
                    data.chapter,
                    sectionLabel(chunk.headings),
                  )
                )
              }
            >
              {strings.corpusAskAbout}
            </button>
          </div>
        </article>
      ))}

      <PopularQuestions
        language={language}
        book={book}
        chapter={chapter}
        onPick={onAsk}
      />

      {selection !== null ? (
        <SelectionBookmarkMenu selection={selection} strings={strings} onSave={handleSave} />
      ) : null}
      {flight !== null ? (
        <BookmarkFlight flight={flight} reducedMotion={reducedMotion} onDone={() => setFlight(null)} />
      ) : null}
      {glossary.card !== null ? <GlossaryCard card={glossary.card} strings={strings} /> : null}
      <span className="creader-sr-only" role="status" aria-live="polite">
        {announced ? strings.corpusBookmarkSaved : ""}
      </span>
    </div>
  );
}

export function CorpusReader(props: CorpusReaderProps): JSX.Element {
  const { route, navigate, language, onLanguage, strings, onAsk, onAbout } = props;
  const manifest = useCorpusManifest();
  const prefs = useReaderPrefs();
  const reading = useReadingRecord();
  // Task 204: bumped on a jump-back click so ChapterPane re-scrolls even when the
  // saved anchor already equals the URL anchor.
  const [jumpNonce, setJumpNonce] = useState(0);
  const isChapter = route.book !== null && route.chapter !== null;
  const isShortDetail = route.shorts && route.shortVideoId !== null;

  const books = useMemo<CorpusIndexBook[]>(
    () => (manifest.state === "ready" ? [...manifest.data.books].sort((a, b) => a.order - b.order) : []),
    [manifest],
  );
  const shorts = useMemo<CorpusIndexShort[]>(
    () =>
      manifest.state === "ready" && Array.isArray(manifest.data.shorts?.entries)
        ? [...manifest.data.shorts.entries].sort(compareFlechaPresentation)
        : [],
    [manifest],
  );

  const goChapter = (book: string, chapter: number): void => navigate(corpusPath(book, chapter));
  const goShort = (videoId: string): void => navigate(corpusShortPath(videoId));

  // Task 209: continuous cross-book chapter order for prev/next pagination
  // (…Génesis 50 → Éxodo 1…), derived from the manifest's book/chapter order.
  const flatChapters = useMemo(
    () => books.flatMap((b) => b.chapters.map((c) => ({ book: b.slug, name: b.name, chapter: c.chapter, label: c.label }))),
    [books],
  );
  const flatIndex = isChapter
    ? flatChapters.findIndex((e) => e.book === route.book && e.chapter === route.chapter)
    : -1;
  const prevChapter = flatIndex > 0 ? flatChapters[flatIndex - 1] ?? null : null;
  const nextChapter = flatIndex >= 0 ? flatChapters[flatIndex + 1] ?? null : null;

  // Jump-back pill (Task 204): the persistent reading-point indicator. Navigates
  // to the saved chapter#anchor and bumps the nonce so the scroll always fires.
  const mark = reading.active;
  // Newest published book (corpus-data/latest.json); null hides the notice.
  const latestBook = useLatestBook();
  const markBook = mark !== null ? books.find((b) => b.slug === mark.book) : undefined;
  const bookName = mark !== null ? markBook?.name ?? mark.book : "";
  const markLabel = markBook?.chapters.find((c) => c.chapter === mark?.chapter)?.label;
  const jumpPill =
    mark !== null ? (
      <div className="creader-mark" role="region" aria-label={strings.corpusBookmarkPinLabel}>
        <button
          type="button"
          className="creader-mark__jump"
          aria-label={strings.corpusBookmarkJumpAria(bookName, mark.chapter)}
          onClick={() => {
            setJumpNonce((n) => n + 1);
            navigate(corpusPath(mark.book, mark.chapter, mark.anchor ?? null));
          }}
        >
          <BowSigil className="creader-mark__sigil" />
          <span className="creader-mark__body">
            <span className="creader-mark__label">{strings.corpusBookmarkJump}</span>
            <span className="creader-mark__where">
              {chapterHeading(bookName, mark.chapter, markLabel)}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="creader-mark__clear"
          aria-label={strings.corpusBookmarkClear}
          title={strings.corpusBookmarkClear}
          onClick={() => reading.clear()}
        >
          ×
        </button>
      </div>
    ) : null;

  // Task 224: the unified topbar — same bones as chat/connections, with the
  // reader's readability controls as view extras.
  const topbar = (
    <Topbar
      variant="explore"
      active="corpus"
      strings={strings}
      language={language}
      onLanguageChange={(e) => onLanguage(e.target.value === "en" ? "en" : "es")}
      onAbout={onAbout}
      extras={isChapter || isShortDetail ? <ReadabilityControls prefs={prefs} strings={strings} /> : null}
    />
  );

  if (manifest.state === "loading") {
    return (
      <div className="creader" data-reader-type={prefs.prefs.type}>
        {topbar}
        <div className="creader-body">
          <p className="creader-note">{strings.corpusLoading}</p>
        </div>
      </div>
    );
  }
  if (manifest.state === "error" || manifest.state === "empty") {
    return (
      <div className="creader" data-reader-type={prefs.prefs.type}>
        {topbar}
        <div className="creader-body">
          <p className="creader-note creader-note--err">{strings.corpusError}</p>
        </div>
      </div>
    );
  }

  if (route.shorts) {
    if (route.shortVideoId !== null) {
      if (!shorts.some((entry) => entry.video_id === route.shortVideoId)) {
        return (
          <div className="creader creader--short" data-reader-type={prefs.prefs.type}>
            {topbar}
            <div className="creader-body">
              <div className="freader-missing">
                <p className="creader-note creader-note--err">{strings.corpusShortNotFound}</p>
                <button
                  type="button"
                  className="freader-aside__back"
                  onClick={() => navigate(corpusShortPath())}
                >
                  ← {language === "es" ? "Volver a todas las Flechas" : "Back to all Flechas"}
                </button>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div
          className="creader creader--chapter creader--short"
          data-reader-type={prefs.prefs.type}
        >
          {topbar}
          <div className="creader-body">
            <FlechasPane
              videoId={route.shortVideoId}
              entries={shorts}
              anchor={route.anchor}
              scale={prefs.scale}
              language={language}
              strings={strings}
              onAsk={onAsk}
              onOpen={goShort}
              onIndex={() => navigate(corpusShortPath())}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="creader creader--library creader--shorts" data-reader-type={prefs.prefs.type}>
        {topbar}
        <div className="creader-body">
          <FlechasShelf entries={shorts} language={language} onOpen={goShort} standalone />
        </div>
      </div>
    );
  }

  if (isChapter && route.book && route.chapter !== null) {
    return (
      <div className="creader creader--chapter" data-reader-type={prefs.prefs.type}>
        {topbar}
        <div className="creader-body">
        <div className="creader-layout">
          <aside className="creader-aside">
            <BookNav
              books={books}
              activeBook={route.book}
              activeChapter={route.chapter}
              compact
              strings={strings}
              onChapter={goChapter}
              bookmark={reading.active}
            />
          </aside>
          <main className="creader-main">
            <ChapterPane
              book={route.book}
              chapter={route.chapter}
              anchor={route.anchor}
              scale={prefs.scale}
              strings={strings}
              language={language}
              onAsk={onAsk}
              onSaveBookmark={reading.save}
              activeBookmark={reading.active}
              jumpNonce={jumpNonce}
            />
            {prevChapter !== null || nextChapter !== null ? (
              <nav className="creader-pagenav" aria-label={strings.corpusPageNavLabel}>
                {prevChapter !== null ? (
                  <button
                    type="button"
                    className="creader-pagenav__btn creader-pagenav__btn--prev"
                    onClick={() => goChapter(prevChapter.book, prevChapter.chapter)}
                  >
                    <span className="creader-pagenav__dir">← {strings.corpusPrevChapter}</span>
                    <span className="creader-pagenav__where">
                      {chapterHeading(prevChapter.name, prevChapter.chapter, prevChapter.label)}
                    </span>
                  </button>
                ) : (
                  <span />
                )}
                {nextChapter !== null ? (
                  <button
                    type="button"
                    className="creader-pagenav__btn creader-pagenav__btn--next"
                    onClick={() => goChapter(nextChapter.book, nextChapter.chapter)}
                  >
                    <span className="creader-pagenav__dir">{strings.corpusNextChapter} →</span>
                    <span className="creader-pagenav__where">
                      {chapterHeading(nextChapter.name, nextChapter.chapter, nextChapter.label)}
                    </span>
                  </button>
                ) : (
                  <span />
                )}
              </nav>
            ) : null}
          </main>
        </div>
        </div>
        {jumpPill}
      </div>
    );
  }

  // Library index.
  return (
    <div className="creader creader--library" data-reader-type={prefs.prefs.type}>
      {topbar}
      <div className="creader-body">
      <CorpusLibrary
        books={books}
        shorts={shorts}
        onChapter={goChapter}
        onShort={goShort}
        onShortIndex={() => navigate(corpusShortPath())}
        language={language}
        latest={latestBook}
        afterHero={
          /* Task 224: "Continúa leyendo" — the saved reading point as a proper
             plaque above the wall, not just the bottom pill. */
          mark !== null ? (
            <button
              type="button"
              className="creader-continue"
              onClick={() => {
                setJumpNonce((n) => n + 1);
                navigate(corpusPath(mark.book, mark.chapter, mark.anchor ?? null));
              }}
            >
              <BowSigil className="creader-continue__sigil" />
              <span className="creader-continue__body">
                <span className="creader-continue__kicker">{strings.corpusContinueReading}</span>
                <span className="creader-continue__where">
                  {chapterHeading(bookName, mark.chapter, markLabel)}
                </span>
                {mark.snippet.length > 0 ? (
                  <span className="creader-continue__snippet">“{mark.snippet}”</span>
                ) : null}
              </span>
              <span className="creader-continue__go" aria-hidden="true">
                →
              </span>
            </button>
          ) : null
        }
      />
      </div>
      {jumpPill}
    </div>
  );
}
